import { ref, computed } from 'vue';

// 全局配置缓存
let globalConfig = null;
let configPromise = null;

export function useCloudProvider() {

  // 当前云服务提供者（只读，不允许切换）
  const currentProvider = ref(null); // 初始为null，避免竞态条件
  const isProviderSwitchable = ref(false);
  const configLoaded = ref(false);

  // 从API获取配置（带缓存）
  const loadConfig = async () => {
    // 如果已经有缓存的配置，直接使用
    if (globalConfig) {
      currentProvider.value = globalConfig.cloudProvider || 'gcp';
      isProviderSwitchable.value = globalConfig.allowProviderSwitch || false;
      configLoaded.value = true;
      return globalConfig;
    }

    // 如果正在加载中，等待现有的请求
    if (configPromise) {
      try {
        const config = await configPromise;
        currentProvider.value = config.cloudProvider || 'gcp';
        isProviderSwitchable.value = config.allowProviderSwitch || false;
        configLoaded.value = true;
        return config;
      } catch (error) {
        console.warn('等待配置加载失败，使用默认值:', error);
        currentProvider.value = 'gcp';
        isProviderSwitchable.value = false;
        configLoaded.value = true;
        return {
          cloudProvider: 'gcp',
          allowProviderSwitch: false
        };
      }
    }

    // 创建新的配置请求
    configPromise = (async () => {
      try {
        const response = await fetch('/api/config');
        const config = await response.json();
        globalConfig = config; // 缓存配置
        return config;
      } catch (error) {
        console.warn('无法加载云服务配置，使用默认值:', error);
        globalConfig = {
          cloudProvider: 'gcp',
          allowProviderSwitch: false
        };
        return globalConfig;
      }
    })();

    try {
      const config = await configPromise;
      currentProvider.value = config.cloudProvider || 'gcp';
      isProviderSwitchable.value = config.allowProviderSwitch || false;
      configLoaded.value = true;
      console.log('云服务配置加载完成:', currentProvider.value);
      return config;
    } catch (error) {
      console.warn('配置加载失败，使用默认值:', error);
      currentProvider.value = 'gcp';
      isProviderSwitchable.value = false;
      configLoaded.value = true;
      return {
        cloudProvider: 'gcp',
        allowProviderSwitch: false
      };
    }
  };

  // 初始化时加载配置
  if (typeof window !== 'undefined') {
    loadConfig();
  }

  // 云服务提供者配置
  const providers = {
    gcp: {
      name: 'Google Cloud Platform',
      shortName: 'GCP',
      icon: 'fab fa-google',
      regions: [
        { value: 'us-central1', label: 'US Central (Iowa)' },
        { value: 'us-east1', label: 'US East (South Carolina)' },
        { value: 'us-west1', label: 'US West (Oregon)' },
        { value: 'asia-east1', label: 'Asia East (Taiwan)' },
        { value: 'europe-west1', label: 'Europe West (Belgium)' }
      ],
      instanceTypes: {
        e2: [
          { value: 'e2-micro', label: 'e2-micro', spec: '0.5-2 vCPU, 1 GB RAM' },
          { value: 'e2-small', label: 'e2-small', spec: '0.5-2 vCPU, 2 GB RAM' },
          { value: 'e2-medium', label: 'e2-medium', spec: '1-2 vCPU, 4 GB RAM' }
        ],
        n1: [
          { value: 'n1-standard-16', label: 'n1-standard-16', spec: '16 vCPU, 60 GB RAM' },
          { value: 'n1-standard-32', label: 'n1-standard-32', spec: '32 vCPU, 120 GB RAM' }
        ]
      },
      gpuTypes: [
        { value: 'nvidia-l4', label: 'NVIDIA L4' },
        { value: 'nvidia-tesla-t4', label: 'NVIDIA T4' },
        { value: 'nvidia-tesla-a100', label: 'NVIDIA A100 40G' }
      ]
    },
    aws: {
      name: 'Amazon Web Services',
      shortName: 'AWS',
      icon: 'fab fa-aws',
      regions: [
        { value: 'us-east-1', label: 'US East (N. Virginia)' },
        { value: 'us-west-2', label: 'US West (Oregon)' },
        { value: 'eu-west-1', label: 'Europe (Ireland)' },
        { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' }
      ],
      instanceTypes: {
        t3: [
          { value: 't3.micro', label: 't3.micro', spec: '2 vCPU, 1 GB RAM' },
          { value: 't3.small', label: 't3.small', spec: '2 vCPU, 2 GB RAM' },
          { value: 't3.medium', label: 't3.medium', spec: '2 vCPU, 4 GB RAM' }
        ],
        m5: [
          { value: 'm5.large', label: 'm5.large', spec: '2 vCPU, 8 GB RAM' },
          { value: 'm5.xlarge', label: 'm5.xlarge', spec: '4 vCPU, 16 GB RAM' },
          { value: 'm5.2xlarge', label: 'm5.2xlarge', spec: '8 vCPU, 32 GB RAM' }
        ]
      },
      gpuTypes: [
        { value: 'g4dn.xlarge', label: 'G4dn.xlarge (T4)' },
        { value: 'p3.2xlarge', label: 'p3.2xlarge (V100)' },
        { value: 'p4d.24xlarge', label: 'p4d.24xlarge (A100)' }
      ]
    }
  };

  // 当前提供者的配置
  const currentProviderConfig = computed(() => providers[currentProvider.value]);

  // isProviderSwitchable 已在上面定义

  // 切换云服务提供者（仅在允许切换时可用）
  const switchProvider = (provider) => {
    if (!isProviderSwitchable.value) {
      console.warn('云服务提供者切换已禁用，当前提供者:', currentProvider.value);
      return;
    }

    if (providers[provider]) {
      currentProvider.value = provider;
      // 可以在这里添加其他切换逻辑，比如重新加载资源等
    }
  };

  // 获取当前提供者的实例类型
  const getInstanceTypes = (category) => {
    return currentProviderConfig.value.instanceTypes[category] || [];
  };

  // 获取当前提供者的GPU类型
  const getGpuTypes = () => {
    return currentProviderConfig.value.gpuTypes || [];
  };

  // 获取当前提供者的区域
  const getRegions = () => {
    return currentProviderConfig.value.regions || [];
  };

  // 获取完整配置（包括 Cognito 配置）
  const getFullConfig = async () => {
    return await loadConfig();
  };

  return {
    currentProvider,
    currentProviderConfig,
    providers,
    isProviderSwitchable,
    configLoaded,
    switchProvider,
    getInstanceTypes,
    getGpuTypes,
    getRegions,
    loadConfig,
    getFullConfig
  };
}

